/**
 * Keeps a battle that has dropped to 30 fps from staying there (M34, Kevin: "the frame rate drop before a pause is
 * still happening after big waves too").
 *
 * The trap: the display only shows frames on its vsync (16.7 ms at 60 Hz), and the fixed-step clock runs as many 20 Hz
 * ticks as the frame's time owes. At ×3 a 60 Hz frame owes one tick; a 30 Hz frame owes two. With a big wave on the
 * board a frame that does one tick fits in 16.7 ms but one that does two does not — so once ONE frame is late (a hitch,
 * a burst of deaths, a garbage collection), the next owes two ticks, so it is late too, so the one after owes two… The
 * battle sits at 30 fps doing exactly the work 60 fps would have done, and nothing in it ever runs one cheap frame to
 * get back. Only a pause (or the app going to the background, which pauses) does: the paused frames run no ticks, are
 * on time, and the first frame after owes one tick again. A quieter board later does not free it either, as long as
 * a frame's work plus two ticks still overruns the vsync.
 *
 * The pacer measures what a tick costs (`c`, the sim and its per-tick view hooks) and what the rest of the frame costs
 * (`R`, views, HUD and render submission), and when the ticks owed would not fit one vsync but fewer would, runs only
 * the ones that fit: the sim simply loses that moment (a tick or two, as if the frame had come on time — deterministic,
 * so nothing but the wall-clock pace changes), the frame is on time, and the next frame owes one tick again.
 *
 * It never slows the game for a device that cannot hold the display's rate anyway: when even the ticks a frame on time
 * would owe do not fit (`R + c·nominal > budget`), it runs everything owed, as before. And it checks itself: a frame
 * it held back must be followed by an on-time frame. When it is not although the held frame's JavaScript fitted, the
 * pace is set elsewhere (the GPU, a 30 Hz cap in Low Power Mode); after `Strikes` such holds in a row it stands down
 * for a while, doubling each time (a one-off hitch after a hold is not a strike's worth of evidence on its own).
 *
 * Pure: the level loop passes frame and cost times, so it is unit-tested without a browser.
 */
export class TickPacer {
  /**
   * Share of the vsync interval the measured JavaScript may fill. The browser's own work (style, compositing, GC) is
   * unseen, so not all of it; but a hold that was too optimistic costs only a tick, and the self-check below stands
   * down when holds keep failing, so the budget leans generous.
   */
  static readonly Budget = 0.95;
  /** Smoothing of the measured costs (per frame). */
  static readonly Smoothing = 0.15;
  /** A frame is late when its interval is this many vsyncs or more. */
  static readonly LateVsyncs = 1.5;
  /** First stand-down after a hold that did not bring an on-time frame (s), doubling to `MaxBackoffSeconds`. */
  static readonly BackoffSeconds = 1;
  static readonly MaxBackoffSeconds = 16;
  /** Holds in a row that did not bring an on-time frame before the pacer stands down. */
  static readonly Strikes = 3;
  /** Frame intervals shorter than this are not a display's vsync (a resumed loop's first frame). */
  static readonly MinVsyncSeconds = 1 / 130;

  /** The display's frame interval (s): 60 Hz until frames show it is faster (a ProMotion display unlocked to 120). */
  private vsync = 1 / 60;
  private fastFrames = 0;
  /** Smoothed ms of one tick (with its view hooks), and of the rest of a frame that ticked. 0: not measured yet. */
  private tickMs = 0;
  private restMs = 0;
  /** The last frame was held back (fewer ticks than owed); whether the frame after it came on time decides the next. */
  private held = false;
  /** The last frame's measured JavaScript (ms), ticks and the rest. */
  private lastJsMs = 0;
  private strikes = 0;
  private standDown = 0;
  private backoff = TickPacer.BackoffSeconds;
  /** Ticks not run because they would have made the frame late, since the level began (for the diagnostics). */
  dropped = 0;

  /** The display's frame interval the pacer is budgeting for (s). */
  get vsyncSeconds(): number { return this.vsync; }
  /** Smoothed cost of a tick and of the rest of a ticking frame (ms), as measured. */
  get costs(): { tickMs: number; restMs: number } { return { tickMs: this.tickMs, restMs: this.restMs }; }

  /**
   * How many of `owed` ticks this frame should run. `frameSeconds`: the real interval since the last frame;
   * `ticksPerSecond`: the sim ticks one real second owes now (speed × hit-stop × 20), to know what an on-time frame owes.
   */
  limit(owed: number, frameSeconds: number, ticksPerSecond: number): number {
    this.watchVsync(frameSeconds);
    const late = frameSeconds >= this.vsync * TickPacer.LateVsyncs;
    if (this.held) {                                       // did holding back last frame bring this one on time?
      this.held = false;
      if (!late) { this.strikes = 0; this.backoff = TickPacer.BackoffSeconds; }
      else if (this.lastJsMs <= this.vsync * 1000 * TickPacer.Budget && ++this.strikes >= TickPacer.Strikes) {
        this.strikes = 0;
        this.standDown = this.backoff;
        this.backoff = Math.min(TickPacer.MaxBackoffSeconds, this.backoff * 2);
      }
    }
    if (this.standDown > 0) { this.standDown -= frameSeconds; return owed; }
    if (owed <= 1 || this.tickMs <= 0) return owed;        // nothing to trim, or nothing measured yet
    const budget = this.vsync * 1000 * TickPacer.Budget - this.restMs;
    const fit = Math.floor(budget / this.tickMs);
    if (fit >= owed) return owed;                          // it all fits: run it
    // What a frame on time owes (rounded up): if not even that fits, the display's rate is out of reach whatever
    // this frame does, and holding back would only slow the game down — run everything owed, as before.
    const onTime = Math.max(1, Math.ceil(ticksPerSecond * this.vsync - 1e-9));
    if (fit < onTime) return owed;
    this.held = true;
    this.dropped += owed - fit;
    return fit;
  }

  /** After a frame: it ran `ticks` ticks costing `tickMs` in all, and the rest of its JavaScript cost `restMs`. */
  observe(ticks: number, tickMs: number, restMs: number): void {
    this.lastJsMs = tickMs + restMs;
    if (ticks <= 0) return;                               // a frame without ticks says nothing about either cost
    const k = TickPacer.Smoothing, per = tickMs / ticks;
    this.tickMs = this.tickMs > 0 ? this.tickMs + (per - this.tickMs) * k : per;
    this.restMs = this.restMs > 0 ? this.restMs + (restMs - this.restMs) * k : restMs;
  }

  /** The level was paused or resumed: the next frame's interval says nothing about a hold. */
  reset(): void { this.held = false; }

  /**
   * 60 Hz is assumed; a run of frames clearly shorter than that (a 120 Hz display) moves the budget to it. Never
   * learnt from long frames: a battle stuck at 30 fps would otherwise teach the pacer that 30 is the display's rate.
   */
  private watchVsync(dt: number): void {
    if (dt < TickPacer.MinVsyncSeconds) return;
    if (dt < 0.75 / 60) { if (++this.fastFrames >= 30) this.vsync = Math.max(TickPacer.MinVsyncSeconds, Math.min(this.vsync, dt)); }
    else this.fastFrames = 0;
  }
}
